/* eslint-disable @typescript-eslint/no-var-requires */
import '@fontsource/roboto-condensed'
import '@fontsource/ubuntu'

import * as React from 'react'

import Close from '@mui/icons-material/Close'
import { Snackbar } from '@mui/material'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import _ from 'lodash'
// import Lottie from 'lottie-react'
import { LottieComponentProps, LottieRef } from 'lottie-react'
import { match, MatchResult, pathToRegexp } from 'path-to-regexp'

import { useDispatch, useHistory, useSelector } from 'domains/application/hooks'
import { reloadIfStale } from 'domains/application/appVersion'
import 'lib/fonts.css'
import 'react-datepicker/dist/react-datepicker.css'
import slice from 'domains/application/slice'

import Button, { IconButton } from 'lib/Button'
import { HistoryLink } from 'lib/Link'

// TODO: fix loadable to do async imports of pages
// NOTE: this looks like weird imports because they should be runtime imports
//  and not compile time imports like the ones at the top of the page
const InvitesPage = React.lazy(() => import('domains/auth/InvitesPage'))
const LoginPage = React.lazy(() => import('domains/auth/LoginPage'))
const RegisterPage = React.lazy(() => import('domains/auth/RegisterPage'))
const ResetPasswordPage = React.lazy(
  () => import('domains/auth/ResetPasswordPage'),
)
const SettingsPage = React.lazy(() => import('domains/auth/SettingsPage'))
const InvoiceResetPage = React.lazy(
  () => import('domains/organization/InvoiceResetPage'),
)
const CustomerPage = React.lazy(
  () => import('domains/organization/CustomerPage'),
)
const CustomersPage = React.lazy(
  () => import('domains/organization/CustomersPage'),
)
const FuelCardPage = React.lazy(
  () => import('domains/organization/FuelCardPage'),
)
const FuelCardsPage = React.lazy(
  () => import('domains/organization/FuelCardsPage'),
)
const FuelCardTransactionItemsPage = React.lazy(
  () => import('domains/organization/FuelCardTransactionItemsPage'),
)
const MoneyCodesPage = React.lazy(
  () => import('domains/organization/MoneyCodesPage'),
)

const FuelCardTransactionItemPage = React.lazy(
  () => import('domains/organization/FuelCardTransactionItemPage'),
)
const ImportPage = React.lazy(() => import('domains/organization/ImportPage'))
const InvoicePage = React.lazy(() => import('domains/organization/InvoicePage'))
const InvoicesPage = React.lazy(
  () => import('domains/organization/InvoicesPage'),
)
const ReceiptsPage = React.lazy(
  () => import('domains/organization/ReceiptsPage'),
)
const NewReceiptPage = React.lazy(
  () => import('domains/organization/NewReceiptPage'),
)
const ReceiptDetailPage = React.lazy(
  () => import('domains/organization/ReceiptDetailPage'),
)
const JobsPage = React.lazy(() => import('domains/organization/JobsPage'))
const UserPage = React.lazy(() => import('domains/organization/UserPage'))
const UsersPage = React.lazy(() => import('domains/organization/UsersPage'))
const VehiclePage = React.lazy(() => import('domains/vehicle/VehiclePage'))
const ExternalDriversPage = React.lazy(
  () => import('domains/organization/ExternalDriversPage'),
)
const DriversPage = React.lazy(() => import('domains/organization/DriversPage'))
const SpolStatementCheckPage = React.lazy(
  () => import('domains/organization/spol/SpolStatementCheckPage'),
)
const VendorPricingsPage = React.lazy(
  () => import('domains/organization/vendor-pricing/VendorPricingsPage'),
)
const PriceExceptionsPage = React.lazy(
  () => import('domains/organization/vendor-pricing/PriceExceptionsPage'),
)
const LocationVendorMappingsPage = React.lazy(
  () => import('domains/organization/vendor-pricing/LocationVendorMappingsPage'),
)
const VendorFranchiseSitesPage = React.lazy(
  () => import('domains/organization/vendor-pricing/VendorFranchiseSitesPage'),
)
const RawEfsXmlPage = React.lazy(
  () => import('domains/organization/RawEfsXmlPage'),
)
const PricingGroupsPage = React.lazy(
  () => import('domains/organization/vendor-pricing/PricingGroupsPage'),
)
const LocationsPage = React.lazy(
  () => import('domains/organization/LocationsPage'),
)

// TODO: replace keys usage with
// https://github.com/pillarjs/path-to-regexp?tab=readme-ov-file#match
type RouteMatch = [
  RegExp,
  (path: string) => MatchResult<Record<string, string>> | false,
]
type Route = [RouteMatch[0], RouteMatch[1], React.JSX.Element]

const makeRoute = (path: string) => {
  const { regexp } = pathToRegexp(path)
  return [regexp, match(path)] as RouteMatch
}

const lottieData: {
  json: LottieComponentProps['animationData']
  Lottie: React.FC<LottieComponentProps> | null
  fetching: boolean
} = {
  json: undefined,
  fetching: false,
  Lottie: null,
}

const ErrorPage = () => {
  const [element, setElement] = React.useState(<></>)
  const [count, setCount] = React.useState(5)
  const [render, forceRender] = React.useState(0)
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const lottieRef: LottieRef = React.useRef(null)
  const path = '/app'

  React.useEffect(() => {
    if (lottieData.json == null) {
      import('lottie/404.json').then((json) => {
        lottieData.json = json.default
        forceRender(render + 1)
      })
    }

    if (lottieData.Lottie == null) {
      import('lottie-react').then((m) => {
        lottieData.Lottie = m.default
        forceRender(render + 1)
      })
    }
  }, [lottieData.json, lottieData.Lottie, render, forceRender])

  React.useEffect(() => {
    if (lottieData.Lottie == null || lottieData.json == null) {
      return
    }

    setElement(
      <lottieData.Lottie
        style={{ height: 400, width: 400 }}
        animationData={lottieData.json}
        loop
        lottieRef={lottieRef}
      />,
    )
  }, [lottieData.Lottie, lottieData.json])

  React.useEffect(() => {
    setTimeout(() => {
      if (count === 1) {
        window.location.assign('/')
      } else {
        setCount(count - 1)
      }
    }, 1000)
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [count, setCount])

  React.useEffect(() => {
    lottieRef.current?.play()
  })

  return (
    <Box
      position='absolute'
      width='100vw'
      height='100vh'
      display='flex'
      justifyContent='center'
      alignItems='center'
    >
      {element != null && (
        <Box>
          {element}
          <Box display='flex' alignItems='center' justifyContent='center'>
            <Box mr={10}>
              <Button size='large' variant='contained' to={path}>
                Redirecting in {count} ...
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

const Redirect = ({ to }: { to: string }) => {
  const history = useHistory()
  React.useEffect(() => {
    history.push(to)
  }, [to])

  return <></>
}

const routes: Route[] = [
  [...makeRoute('/invites'), <InvitesPage key='InvitesPage' />],
  [...makeRoute('/register'), <RegisterPage key='RegisterPage' />],
  [...makeRoute('/login'), <LoginPage key='LoginPage' />],
  [
    ...makeRoute('/reset-password/:token'),
    <ResetPasswordPage key='ResetPasswordPage' />,
  ],
  [
    ...makeRoute('/app'),
    <Redirect key='/app' to='/app/fuel-card-transaction-items' />,
  ],
  [...makeRoute('/app/vehicles'), <VehiclePage key='VehiclePage' />],
  [...makeRoute('/app/users/new'), <UsersPage creating key='UsersPage' />],
  [...makeRoute('/app/users/:id'), <UserPage key='UserPage' />],
  [...makeRoute('/app/users'), <UsersPage key='UsersPage' />],
  [
    ...makeRoute('/app/external-drivers'),
    <ExternalDriversPage key='ExternalDriversPage' />,
  ],
  [...makeRoute('/app/drivers'), <DriversPage key='DriversPage' />],
  [...makeRoute('/app/fuel-cards'), <FuelCardsPage key='FuelCardsPage' />],
  [...makeRoute('/app/fuel-cards/:id'), <FuelCardPage key='FuelCardPage' />],
  [
    ...makeRoute('/app/customers/new'),
    <CustomersPage creating key='CustomersPage' />,
  ],
  [
    ...makeRoute('/app/customers/:id/new-invoice-location'),
    <CustomerPage creatingInvoiceLocation key='CustomerPage' />,
  ],
  [...makeRoute('/app/customers/:id'), <CustomerPage key='CustomerPage' />],
  [...makeRoute('/app/customers'), <CustomersPage key='CustomersPage' />],
  [
    ...makeRoute('/app/fuel-card-transaction-items/new'),
    <FuelCardTransactionItemsPage
      creating
      key='FuelCardTransactionItemsPage'
    />,
  ],
  [
    ...makeRoute('/app/fuel-card-transaction-items'),
    <FuelCardTransactionItemsPage key='FuelCardTransactionItemsPage' />,
  ],
  [
    ...makeRoute('/app/fuel-card-transaction-items/add-to-invoice'),
    <FuelCardTransactionItemsPage
      key='FuelCardTransactionItemsPage'
      adding={true}
    />,
  ],
  [
    ...makeRoute('/app/fuel-card-transaction-items/:id/raw-efs'),
    <RawEfsXmlPage key='RawEfsXmlPage' />,
  ],
  [
    ...makeRoute('/app/fuel-card-transaction-items/:id'),
    <FuelCardTransactionItemPage key='FuelCardTransactionItemPage' />,
  ],
  [...makeRoute('/app/money-codes'), <MoneyCodesPage key='MoneyCodesPage' />],
  [
    ...makeRoute('/app/money-codes/add-to-invoice'),
    <MoneyCodesPage adding key='MoneyCodesPage' />,
  ],
  [
    ...makeRoute('/app/money-codes/:id'),
    <MoneyCodesPage key='MoneyCodesPage' />,
  ],
  [
    ...makeRoute('/app/invoices/new'),
    <InvoicesPage creating key='InvoicesPage' />,
  ],
  [...makeRoute('/app/invoices/:id'), <InvoicePage key='InvoicePage' />],
  [...makeRoute('/app/invoices'), <InvoicesPage key='InvoicesPage' />],
  [...makeRoute('/app/receipts/new'), <NewReceiptPage key='NewReceiptPage' />],
  [...makeRoute('/app/receipts/:id'), <ReceiptDetailPage key='ReceiptDetailPage' />],
  [...makeRoute('/app/receipts'), <ReceiptsPage key='ReceiptsPage' />],
  [
    ...makeRoute('/app/settings/invoice-reset'),
    <InvoiceResetPage key='InvoiceResetPage' />,
  ],
  [...makeRoute('/app/settings'), <SettingsPage key='SettingsPage' />],
  [...makeRoute('/app/jobs'), <JobsPage key='JobsPage' />],
  [...makeRoute('/app/import'), <ImportPage key='ImportPage' />],
  [
    ...makeRoute('/app/spol/statement-check'),
    <SpolStatementCheckPage key='SpolStatementCheckPage' />,
  ],
  [
    ...makeRoute('/app/vendor-pricings'),
    <VendorPricingsPage key='VendorPricingsPage' />,
  ],
  [
    ...makeRoute('/app/price-exceptions'),
    <PriceExceptionsPage key='PriceExceptionsPage' />,
  ],
  [
    ...makeRoute('/app/location-vendor-mappings'),
    <LocationVendorMappingsPage key='LocationVendorMappingsPage' />,
  ],
  [
    ...makeRoute('/app/locations'),
    <LocationsPage key='LocationsPage' />,
  ],
  [
    ...makeRoute('/app/vendor-franchise-sites'),
    <VendorFranchiseSitesPage key='VendorFranchiseSitesPage' />,
  ],
  [
    ...makeRoute('/app/pricing-groups'),
    <PricingGroupsPage key='PricingGroupsPage' />,
  ],
  [...makeRoute('{/*rest}'), <ErrorPage key='ErrorPage' />],
]

export const getRoute = function getRoute(
  path: string,
): (typeof routes)[number] {
  const route = _.find(routes, ([regexp, _keys, _comp]) => {
    return regexp.test(path)
  })

  return route as (typeof routes)[0]
}

declare module '@mui/material/styles/createPalette' {
  interface TypeText {
    light?: string
  }
}

const t = createTheme({
  palette: {
    contrastThreshold: 3,
    tonalOffset: 0.4,
  },
})

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: false // removes the `xs` breakpoint
    sm: false
    md: false
    lg: false
    xl: true
    mobile: true // adds the `mobile` breakpoint
    tablet: true
    laptop: true
    desktop: true
  }
}

const theme = createTheme({
  breakpoints: {
    values: {
      laptop: 1080,
      tablet: 768,
      mobile: 0,
      desktop: 1280,
      xl: 1500,
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: 'rgb(83, 58, 253)',
      light: '#7a73ff',
      dark: '#7a73ff',
      contrastText: '#fff',
    },
    secondary: {
      main: '#303c75',
      light: '#303c75',
      dark: '#303c75',
      contrastText: '#fff',
    },
    text: {
      primary: '#393e48',
      secondary: '#596171',
      light: 'rgb(196 194 194)',
    },
    success: t.palette.augmentColor({
      color: {
        main: '#d1fab3',
      },
      name: 'success',
    }),
  },
  typography: {
    fontFamily: ['"Roboto Condensed"', 'arial', 'sans-serif', 'system'].join(
      ', ',
    ),
    h4: {
      fontWeight: '700',
    },
    h5: {
      fontWeight: '700',
    },
    h6: {
      fontWeight: '700',
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        fontWeight: 800,
        fontSmooth: 'always',
        fill: '#675dff',
        height: '100vh',
      },
    },
    MuiLink: {
      defaultProps: {
        component: HistoryLink,
      },
    },
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: HistoryLink,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.variant === 'contained' && {
            boxShadow: 'none',
            border: '1px solid',
            ':hover': {
              boxShadow: 'none',
            },
          }),
        }),
      },
    },
    MuiIconButton: {
      defaultProps: {
        LinkComponent: HistoryLink,
      },
    },
    MuiChip: {
      defaultProps: {
        sx(_theme) {
          return {
            borderRadius: 1,
          }
        },
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.color === 'success' &&
            ownerState.variant === 'filled' && {
              borderColor: theme.palette.success.dark,
              color: theme.palette.success.dark,
              borderWidth: 1,
              borderStyle: 'solid',
            }),
        }),
      },
    },
  },
})

export const Application = function Application({
  className,
}: {
  className?: string
}) {
  const path = useSelector((state) => state.application.path)
  const lastPath = React.useRef(path)
  const [_regexp, _keys, Component] = React.useMemo(
    () => getRoute(path),
    [path],
  ) as Route
  // TODO: use DeferredComponent but routes need to be able to handle no data
  // const DeferredComponent = React.useDeferredValue(Component)

  const dispatch = useDispatch()
  const snacks = useSelector((state) => state.application.snacks) ?? []

  const popSnack = React.useCallback(() => {
    dispatch(slice.actions.popSnack())
  }, [dispatch])

  React.useEffect(() => {
    if (lastPath.current === path) {
      return
    }
    // Reload if a new version was deployed while user was on previous page
    reloadIfStale()
    lastPath.current = path
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [path])

  return (
    <ThemeProvider theme={theme}>
      <Box className={className} color='text.primary'>
        <Snackbar
          ClickAwayListenerProps={{
            mouseEvent: false,
            touchEvent: false,
          }}
          open={snacks.length > 0}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={
            snacks[0]?.autoDismiss != null ? snacks[0]?.duration ?? 6000 : null
          }
          onClose={popSnack}
          message={snacks[0]?.message}
          action={
            <>
              {/* <Button color='secondary' size='small' onClick={popSnack}>
                UNDO
              </Button> */}
              <IconButton
                size='small'
                aria-label='close'
                color='inherit'
                onClick={popSnack}
              >
                <Close fontSize='small' />
              </IconButton>
            </>
          }
        >
          <Alert
            onClose={popSnack}
            variant='filled'
            sx={{ width: '100%' }}
            {...(snacks[0]?.props ?? {})}
            color={snacks?.[0]?.color != null ? snacks?.[0]?.color : 'success'}
          >
            {snacks[0]?.message}
          </Alert>
        </Snackbar>
        {global.__APP__.env === 'dev' && (
          <Box
            typography='body2'
            height={45}
            bgcolor='primary.main'
            color='primary.contrastText'
            display='flex'
            justifyContent='center'
            alignItems='center'
          >
            <Box>dev env</Box>
          </Box>
        )}
        <React.Suspense fallback={null}>{Component}</React.Suspense>
      </Box>
    </ThemeProvider>
  )
}

export default Application
